/**
 * Equalizer for Chrome browser — audio equalizer / volume booster
 *
 * Declares one web-accessible-resource entry matched to every http(s) origin. Beyond two
 * site-specific patterns it exposes a catch-all glob that matches any file containing a
 * dot, with no `use_dynamic_url` — so effectively the whole package is fetchable from any
 * page at a fixed URL. The numbered PNG icons under `icons/` load through `probeImage`
 * with no CORS grant, making a load conclusive.
 *
 * Manifest verified against version 3.7.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'abikfbojmghmfjdjlbagiamkinbmbaic';

export const equalizer: ExtensionDefinition = {
  id: ID,
  name: 'Equalizer for Chrome browser',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 500_000,
  notes:
    'A catch-all WAR glob on every http(s) origin with no dynamic URL exposes the whole ' +
    'package. The icons/128.png image loads without a CORS grant and is conclusive.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load icons/128.png via <img> — a packaged icon exposed to every origin, ' +
        'readable without a CORS grant.',
      weight: 1,
      run: () => probeImage(ID, 'icons/128.png'),
    },
    {
      id: 'war-settings-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch settings.html — a static page reachable via the catch-all glob, ' +
        'corroborating the identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'settings.html'),
    },
    {
      id: 'war-options-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch options.js — a third path independent of the icon and settings page.',
      weight: 0.5,
      run: () => probeFetch(ID, 'options.js'),
    },
  ],
};
